import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';

import {
  AuthenticationService,
  CredentialsService,
  I18nService,
  Logger,
} from '@app/core';
import { BaseComponent } from '@app/core/helperclasses/basecomponent';
import { environment } from '@env/environment';
import { ThemingService } from '@app/core/helperclasses/theming.service';

const log = new Logger('Login');

import { UntilDestroy } from '@ngneat/until-destroy';
import { ResetPasswordDialogComponent } from '@app/dialogs/reset-password-dialog/reset-password-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AppLoadService } from '@app/app-load.service';
import { ShellService } from '@app/shell/shell.service';
import { BasicSettings } from '@app/models/core/general';
import { RoutingService } from '@app/core/routing.service';
@UntilDestroy()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent extends BaseComponent implements OnInit {
  currentYear: number = new Date().getFullYear();
  version: string | null = environment.version;
  companyName: string = environment.companyName;
  demoCourseUrl: string = environment.demoCourseUrl;
  error!: string;
  loginForm!: UntypedFormGroup;
  isLoading = false;
  basicSettings!: BasicSettings;
  public currentTheme: string = '';
  public showPassword = false;

  public baseUrl = environment.serverUrl + environment.apiVersion;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private i18nService: I18nService,
    private authenticationService: AuthenticationService,
    private credentialsService: CredentialsService,
    public themingService: ThemingService,
    private shellService: ShellService,
    private dialog: MatDialog,
    private appLoadService: AppLoadService,
    private routingService: RoutingService,
  ) {
    super();
    this.createForm();
    this.themingService.theme.subscribe((theme) => {
      this.currentTheme = theme;
    });

    this.getBasicSettings();
  }

  ngOnInit() {}

  getBasicSettings() {
    this.addSubscription(this.shellService.getBasicSettingsHelper()).subscribe(
      (settings) => {
        if (settings) {
          this.basicSettings = settings.data;
        }
      },
    );
  }

  login() {
    this.isLoading = true;

    this.addSubscription(this.authenticationService.login(this.loginForm.value))
      .pipe(
        finalize(() => {
          this.loginForm.markAsPristine();
          this.isLoading = false;
        }),
      )
      .subscribe(
        (credentials) => {
          if (credentials) {
            localStorage.setItem(
              'forceLogout',
              JSON.stringify({
                force_logout_after_minutes:
                  credentials.data.force_logout_after_minutes,
                inactive_after_minutes: credentials.data.inactive_after_minutes,
              }),
            );
            this.authenticationService.forceLogout.next(
              localStorage.getItem('forceLogout'),
            );
            log.debug(`${credentials.data.username} successfully logged in`);
            this.appLoadService.setIsFirstLoad();
            this.router.navigate(['/home'], { replaceUrl: true });
          }
        },
        (error) => {
          log.debug(`Login error: ${error}`);
          this.error = error.error.error_message;
        },
      );
  }

  setLanguage(language: string) {
    this.i18nService.language = language;
  }

  get currentLanguage(): string {
    return this.i18nService.language;
  }

  get languages(): string[] {
    return this.i18nService.supportedLanguages;
  }

  private createForm() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      remember: true,
    });
  }

  openUrl(url: string) {
    window.open(url, '_blank');
  }

  openResetPasswordDialog() {
    this.addSubscription(
      this.dialog
        .open(ResetPasswordDialogComponent, {
          data: {},
        })
        .afterClosed(),
    ).subscribe();
  }
}
